<template>
  <div class="light-panel">
    <div ref="gaugeRef" class="gauge-chart"></div>
    <div ref="chartRef" class="light-chart"></div>
    <div class="light-footer">
      <span>当前状态：{{ lightStatus }}</span>
      <span>今日累计光照：4.8小时</span>
      <span>光照充足率：86%</span>
    </div>
  </div>
</template>

<script setup>
import * as echarts from "echarts";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  xData: {
    type: Array,
    default: () => [],
  },
  lightData: {
    type: Array,
    default: () => [],
  },
  realtimeLight: {
    type: Number,
    default: 0,
  },
});

const gaugeRef = ref(null);
const chartRef = ref(null);
let chartInstance = null;
let gaugeInstance = null;

const option = {
  color: ["#8fe7f5"],
  grid: { top: 60, left: 80, right: 20, bottom: 46 },
  legend: {
    top: 0,
    right: 32,
    icon: "rect",
    itemWidth: 14,
    itemHeight: 14,
    textStyle: { color: "#dff9ff", fontSize: 22 },
    data: ["作物适宜区间：20000-60000 Lux"],
  },
  tooltip: {
    trigger: "axis",
    backgroundColor: "rgba(28, 73, 78, .88)",
    borderColor: "rgba(135, 247, 240, .46)",
    textStyle: { color: "#fff", fontSize: 22 },
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: [],
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(102, 187, 231, .35)" } },
    axisLabel: { color: "#d8f2ff", fontSize: 22 },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 60000,
    name: " / Lux",
    nameTextStyle: { color: "#dff9ff", fontSize: 22, padding: [0, 0, 8, 0] },
    axisLabel: { color: "#d8f2ff", fontSize: 18, formatter: (value) => (value === 0 ? "" : value) },
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { lineStyle: { color: "rgba(116, 206, 207, .12)" } },
  },
  series: [
    {
      name: "作物适宜区间：20000-60000 Lux",
      type: "line",
      smooth: true,
      symbolSize: 0,
      lineStyle: { color: "#b6f4ef", width: 2 },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(176, 244, 239, .66)" },
          { offset: 0.55, color: "rgba(74, 177, 171, .34)" },
          { offset: 1, color: "rgba(18, 80, 76, .05)" },
        ]),
      },
      // markPoint: {
      //   symbol: "roundRect",
      //   symbolSize: [126, 36],
      //   itemStyle: { color: "rgba(129, 230, 222, 0)", borderColor: "rgba(230,255,255,0)" },
      //   label: { color: "#fff", fontSize: 20, fontWeight: "bold" },
      //   data: [{ coord: ["16时", 52], value: "20000-60000 Lux" }],
      // },
      data: [],
    },
  ],
};

const gaugeOption = {
  series: [
    {
      type: "gauge",
      min: 0,
      max: 100000,
      radius: "84%",
      center: ["50%", "55%"],
      startAngle: 220,
      endAngle: -40,
      splitNumber: 10,
      progress: {
        show: true,
        width: 18,
        roundCap: false,
        itemStyle: { color: "#8ef8f1" },
      },
      axisLine: {
        roundCap: false,
        lineStyle: {
          width: 18,
          color: [[1, "rgba(123, 242, 234, .2)"]],
        },
      },
      axisTick: {
        distance: -26,
        length: 8,
        splitNumber: 2,
        lineStyle: { width: 2, color: "#d8fbff" },
      },
      splitLine: {
        distance: -30,
        length: 18,
        lineStyle: { width: 3, color: "#d8fbff" },
      },
      axisLabel: {
        distance: -52,
        color: "#e7fdff",
        fontSize: 16,
        formatter: (value) => (value === 0 ? "0" : value / 1000),
      },
      pointer: { show: false },
      anchor: { show: false },
      detail: {
        valueAnimation: true,
        offsetCenter: [0, "4%"],
        formatter: (value) => `{value|${formatNumber(value)}}{unit| }\n{name|Lux}`,
        rich: {
          value: { color: "#fff", fontSize: 54, fontWeight: "bold", lineHeight: 62 },
          unit: { color: "#fff", fontSize: 20, fontWeight: "bold" },
          name: { color: "#fff", fontSize: 22, fontWeight: "bold", lineHeight: 28 },
        },
      },
      title: { show: false },
      data: [{ value: 0 }],
    },
    {
      type: "gauge",
      min: 0,
      max: 100000,
      radius: "99%",
      center: ["50%", "55%"],
      startAngle: 220,
      endAngle: -40,
      axisLine: { lineStyle: { width: 1, color: [[1, "rgba(132, 246, 239, .35)"]] } },
      splitLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      pointer: { show: false },
      detail: { show: false },
      data: [{ value: 0 }],
    },
  ],
};

function resizeChart() {
  chartInstance && chartInstance.resize();
  gaugeInstance && gaugeInstance.resize();
}

const lightStatus = computed(() => {
  const value = Number(props.realtimeLight) || 0;
  if (value <= 0) return "暂无数据";
  if (value < 20000) return "偏弱";
  if (value <= 60000) return "适宜";
  return "偏强";
});

function formatNumber(value) {
  return `${Math.round(Number(value) || 0)}`;
}

function getLightYAxisMax() {
  const maxValue = Math.max(...props.lightData.map((item) => Number(item) || 0), 60000);
  return Math.ceil((maxValue * 1.15) / 1000) * 1000;
}

function updateChart() {
  if (chartInstance) {
    chartInstance.setOption({
      xAxis: {
        data: props.xData,
      },
      yAxis: {
        max: getLightYAxisMax(),
      },
      series: [
        {
          data: props.lightData,
        },
      ],
    });
  }

  if (gaugeInstance) {
    const value = Number(props.realtimeLight) || 0;
    gaugeInstance.setOption({
      series: [
        {
          data: [{ value }],
        },
        {
          data: [{ value }],
        },
      ],
    });
  }
}

onMounted(() => {
  gaugeInstance = echarts.init(gaugeRef.value);
  gaugeInstance.setOption(gaugeOption);
  chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(option);
  updateChart();
  window.addEventListener("resize", resizeChart);
});

watch(
  () => [props.xData, props.lightData, props.realtimeLight],
  updateChart,
  { deep: true }
);

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chartInstance && chartInstance.dispose();
  gaugeInstance && gaugeInstance.dispose();
});
</script>

<style lang="less" scoped>
.light-panel {
  height: 100%;
  display: grid;
  grid-template-columns: 40% 60%;
  grid-template-rows: 1fr 62px;
  padding: 24px 30px 18px;
  box-sizing: border-box;
  border: 1px solid rgba(76, 205, 197, 0.24);
  background:
    radial-gradient(circle at 24% 45%, rgba(7, 118, 100, 0.38), rgba(2, 32, 34, 0.1) 42%),
    linear-gradient(180deg, rgba(2, 40, 42, 0.8), rgba(3, 23, 26, 0.78));
  box-shadow: inset 0 0 40px rgba(0, 224, 202, 0.08), 0 0 18px rgba(0, 224, 202, 0.08);
}

.gauge-chart {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.light-chart {
  width: 100%;
  height: 100%;
}

.light-footer {
  grid-column: 1 / span 2;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  align-items: end;

  span {
    height: 46px;
    border: 1px solid rgba(138, 255, 246, 0.58);
    background: linear-gradient(180deg, rgba(8, 63, 60, 0.74), rgba(4, 35, 38, 0.86));
    box-shadow: inset 0 0 18px rgba(132, 255, 246, 0.2), 0 0 10px rgba(31, 216, 199, 0.18);
    color: #fff;
    font-size: 22px;
    font-weight: bold;
    line-height: 46px;
    text-align: center;
  }
}
</style>
